
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface CompoundInterestResult {
  futureValue: number;
  totalInterest: number;
  totalContributions: number;
  yearlyBreakdown: Array<{
    year: number;
    startingBalance: number;
    contributions: number;
    interestEarned: number;
    endingBalance: number;
  }>;
}

const CompoundInterestCalculator = () => {
  const [principal, setPrincipal] = useState<string>('10000');
  const [interestRate, setInterestRate] = useState<string>('7');
  const [years, setYears] = useState<string>('10');
  const [compoundingFrequency, setCompoundingFrequency] = useState<string>('12');
  const [additionalContribution, setAdditionalContribution] = useState<string>('100');
  const [contributionFrequency, setContributionFrequency] = useState<string>('12');
  const [result, setResult] = useState<CompoundInterestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculateCompoundInterest = () => {
    try {
      setError(null);
      
      const P = parseFloat(principal);
      const r = parseFloat(interestRate) / 100;
      const t = parseFloat(years);
      const n = parseFloat(compoundingFrequency);
      const PMT = parseFloat(additionalContribution) || 0;
      const contributionFreq = parseFloat(contributionFrequency);

      if (isNaN(P) || P < 0) {
        setError("Please enter a valid initial investment amount");
        return;
      }
      if (isNaN(r) || r < 0) {
        setError("Please enter a valid interest rate");
        return;
      }
      if (isNaN(t) || t <= 0) {
        setError("Please enter a valid number of years");
        return;
      }

      const yearlyBreakdown = [];
      let currentBalance = P;
      let totalContributions = P;

      for (let year = 1; year <= t; year++) {
        const startingBalance = currentBalance;
        const yearlyContributions = PMT * contributionFreq;
        
        // Calculate compound interest with regular contributions
        const periodicRate = r / n;
        const periodsPerYear = n;
        
        // Future value with compound interest and regular payments
        let endingBalance = startingBalance;
        
        // Apply compound interest for each period in the year
        for (let period = 0; period < periodsPerYear; period++) {
          endingBalance = endingBalance * (1 + periodicRate);
          if (contributionFreq >= periodsPerYear) {
            // Add contribution each period if frequency allows
            endingBalance += PMT * (contributionFreq / periodsPerYear);
          }
        }
        
        // If contributions are less frequent than compounding, add them appropriately
        if (contributionFreq < periodsPerYear) {
          endingBalance += yearlyContributions;
        }

        const interestEarned = endingBalance - startingBalance - yearlyContributions;
        totalContributions += yearlyContributions;

        yearlyBreakdown.push({
          year,
          startingBalance,
          contributions: yearlyContributions,
          interestEarned,
          endingBalance
        });

        currentBalance = endingBalance;
      }

      const futureValue = currentBalance;
      const totalInterest = futureValue - totalContributions;

      setResult({
        futureValue,
        totalInterest,
        totalContributions,
        yearlyBreakdown
      });

    } catch (err) {
      setError("Error calculating compound interest. Please check your inputs.");
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Tabs defaultValue="calculator">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="calculator">Calculator</TabsTrigger>
          <TabsTrigger value="breakdown">Year-by-Year Breakdown</TabsTrigger>
        </TabsList>

        <TabsContent value="calculator">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold mb-4">Compound Interest Calculator</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="principal">Initial Investment ($)</Label>
                    <Input
                      id="principal"
                      type="number"
                      value={principal}
                      onChange={(e) => setPrincipal(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="rate">Annual Interest Rate (%)</Label>
                    <Input
                      id="rate"
                      type="number"
                      step="0.01"
                      value={interestRate}
                      onChange={(e) => setInterestRate(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="years">Time Period (Years)</Label>
                    <Input
                      id="years"
                      type="number"
                      value={years}
                      onChange={(e) => setYears(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="compounding">Compounding Frequency</Label>
                    <Select value={compoundingFrequency} onValueChange={setCompoundingFrequency}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Annually</SelectItem>
                        <SelectItem value="2">Semi-annually</SelectItem>
                        <SelectItem value="4">Quarterly</SelectItem>
                        <SelectItem value="12">Monthly</SelectItem>
                        <SelectItem value="52">Weekly</SelectItem>
                        <SelectItem value="365">Daily</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="contribution">Additional Contribution ($)</Label>
                    <Input
                      id="contribution"
                      type="number"
                      value={additionalContribution}
                      onChange={(e) => setAdditionalContribution(e.target.value)}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="contribution-freq">Contribution Frequency</Label>
                    <Select value={contributionFrequency} onValueChange={setContributionFrequency}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Annually</SelectItem>
                        <SelectItem value="2">Semi-annually</SelectItem>
                        <SelectItem value="4">Quarterly</SelectItem>
                        <SelectItem value="12">Monthly</SelectItem>
                        <SelectItem value="52">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button onClick={calculateCompoundInterest} className="w-full">
                    Calculate
                  </Button>

                  {error && (
                    <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
                      {error}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">Results</h3>
                
                {result ? (
                  <div className="space-y-4">
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-medium">Future Value</h4>
                      <p className="text-2xl font-bold text-green-600">
                        ${result.futureValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-medium">Total Interest Earned</h4>
                      <p className="text-xl font-bold text-blue-600">
                        ${result.totalInterest.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium">Total Contributions</h4>
                      <p className="text-xl font-bold">
                        ${result.totalContributions.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">Enter values and calculate to see results</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="breakdown">
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4">Year-by-Year Breakdown</h3>
              
              {result && result.yearlyBreakdown.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Year</TableHead>
                        <TableHead>Starting Balance</TableHead>
                        <TableHead>Contributions</TableHead>
                        <TableHead>Interest Earned</TableHead>
                        <TableHead>Ending Balance</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {result.yearlyBreakdown.map((row) => (
                        <TableRow key={row.year}>
                          <TableCell>{row.year}</TableCell>
                          <TableCell>${row.startingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                          <TableCell>${row.contributions.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                          <TableCell>${row.interestEarned.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                          <TableCell>${row.endingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-gray-500">Calculate compound interest to see yearly breakdown</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CompoundInterestCalculator;
